import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Route, Routes, useLocation } from "react-router-dom";
import {
  AuthProvider,
  useAuth,
  AuthCallback,
  LoginButton,
  UserMenu,
  UsageChip,
  UpgradeModal,
  SignInWall,
  PaymentSuccess,
  PaymentCancel,
} from "./auth";
import {
  ScanLine,
  RefreshCw,
  ShieldCheck,
  AlertTriangle,
  HelpCircle,
  Sparkles,
  Copy,
  FileText,
  Tag,
  Flag,
  ClipboardList,
  Type as TypeIcon,
  Link2,
  Download,
  BookOpen,
  ExternalLink,
  ShieldQuestion,
  Newspaper,
  Globe,
  Search,
  ChevronDown,
  ChevronRight,
  Camera,
  CameraOff,
  Aperture,
  Upload,
  Trash2,
  Info,
  AlertOctagon,
  BrainCircuit,
  Loader2,
} from "lucide-react";
import "@/App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SAMPLE = `BREAKING: Anonymous sources reveal that a top-secret government lab has developed a wearable device that lets ordinary people speak 40 languages instantly, but officials refuse to comment. Insiders say the device will be released "any day now" and will make schools obsolete. Critics on social media are already celebrating what one commentator called "the end of miscommunication as we know it." No independent researchers have been allowed to examine the device, and no peer-reviewed studies have been published.`;

const verdictClass = (v) => {
  if (v === "REAL") return "ts-verdict-real";
  if (v === "FAKE") return "ts-verdict-fake";
  return "ts-verdict-uncertain";
};

const VerdictIcon = ({ v, className }) => {
  if (v === "REAL") return <ShieldCheck className={className} strokeWidth={1.4} />;
  if (v === "FAKE") return <AlertTriangle className={className} strokeWidth={1.4} />;
  return <HelpCircle className={className} strokeWidth={1.4} />;
};

const wordCount = (t) => (t.trim() ? t.trim().split(/\s+/).length : 0);

const TOAST_OPTIONS = {
  style: {
    background: "#121214",
    border: "1px solid #27272a",
    color: "#eaeaea",
    fontFamily: "Manrope, sans-serif",
  },
};

// Build a rendered article view where every evidence quote is underlined with
// the accent color. We scan the text left-to-right and swap in <mark> spans.
const buildAnnotatedNodes = (text, quotes) => {
  if (!text) return [];
  const unique = Array.from(new Set((quotes || []).filter((q) => q && text.includes(q))));
  if (unique.length === 0) return [text];
  // Longest-first so nested quotes don't get partially masked.
  unique.sort((a, b) => b.length - a.length);

  // Walk the string collecting matches then merge overlapping ranges.
  const ranges = [];
  for (const q of unique) {
    let from = 0;
    while (true) {
      const at = text.indexOf(q, from);
      if (at === -1) break;
      ranges.push([at, at + q.length]);
      from = at + q.length;
    }
  }
  ranges.sort((a, b) => a[0] - b[0]);
  const merged = [];
  for (const r of ranges) {
    if (merged.length && r[0] <= merged[merged.length - 1][1]) {
      merged[merged.length - 1][1] = Math.max(merged[merged.length - 1][1], r[1]);
    } else {
      merged.push([...r]);
    }
  }

  const out = [];
  let cursor = 0;
  merged.forEach(([s, e], i) => {
    if (cursor < s) out.push(text.slice(cursor, s));
    out.push(
      <mark key={`cue-${i}-${s}`} className="ts-cue">
        {text.slice(s, e)}
      </mark>
    );
    cursor = e;
  });
  if (cursor < text.length) out.push(text.slice(cursor));
  return out;
};

const SectionTitle = ({ icon: Icon, children }) => (
  <div className="flex items-center gap-2 mb-4">
    <Icon size={13} strokeWidth={1.4} className="text-[color:var(--ts-accent)]" />
    <span className="ts-eyebrow">{children}</span>
  </div>
);

const ListBlock = ({ items, testId, ordered = false }) => {
  if (!items || items.length === 0) {
    return (
      <p data-testid={`${testId}-empty`} className="text-[color:var(--ts-text-muted)] text-sm italic">
        None detected.
      </p>
    );
  }
  return (
    <ul data-testid={testId} className="space-y-3">
      {items.map((it, idx) => (
        <li
          key={`${idx}-${it}`}
          className="ts-fade-up flex gap-3 text-[15px] leading-relaxed text-[color:var(--ts-text)]"
          style={{ animationDelay: `${idx * 70}ms` }}
        >
          <span className="font-mono-alt text-[11px] text-[color:var(--ts-accent)] mt-1.5 shrink-0">
            {ordered ? String(idx + 1).padStart(2, "0") : "—"}
          </span>
          <span className="font-light">{it}</span>
        </li>
      ))}
    </ul>
  );
};

function EmptyState() {
  return (
    <div
      data-testid="empty-state"
      className="ts-panel p-10 md:p-14 flex flex-col items-start"
      style={{ minHeight: 460 }}
    >
      <span className="ts-eyebrow mb-8">Awaiting Input</span>
      <h3 className="font-serif-display text-3xl md:text-4xl leading-tight mb-6">
        Paste an article. <span className="italic text-[color:var(--ts-accent)]">Reveal</span> its
        provenance.
      </h3>
      <p className="text-[color:var(--ts-text-muted)] max-w-md font-light leading-relaxed">
        News Classifier applies a large-language reasoning model to the text you provide and returns a
        verdict, a confidence estimate, a category assignment, and its full chain of reasoning.
      </p>
      <div className="ts-divider w-full my-10" />
      <div className="grid grid-cols-3 gap-6 w-full">
        {[
          ["01", "Verdict", "Real · Fake · Uncertain"],
          ["02", "Category", "Exhaustive taxonomy"],
          ["03", "Reasoning", "Cited textual cues"],
        ].map(([n, t, s]) => (
          <div key={n}>
            <div className="font-mono-alt text-[10.5px] text-[color:var(--ts-accent)] tracking-widest mb-2">
              {n}
            </div>
            <div className="font-serif-display text-lg leading-none mb-1">{t}</div>
            <div className="text-xs text-[color:var(--ts-text-muted)] font-light">{s}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function LoadingState() {
  return (
    <div
      data-testid="loading-state"
      className="ts-panel ts-tracing p-10 md:p-14"
      style={{ minHeight: 460 }}
    >
      <span className="ts-eyebrow mb-8 block">Analyzing</span>
      <h3 className="font-serif-display text-3xl md:text-4xl leading-tight mb-4">
        Gathering evidence, then{" "}
        <span className="italic">reading between the lines</span>…
      </h3>
      <p className="text-[color:var(--ts-text-muted)] font-light text-[13px] mb-8 max-w-md">
        Cross-referencing Google Fact Check, NewsAPI and GDELT — plus a
        URL-heuristic scrutiny of the article&apos;s hero image — before the
        model renders its verdict.
      </p>
      <div className="space-y-4">
        {[80, 60, 90, 45, 70].map((w) => (
          <div
            key={`skeleton-${w}`}
            className="h-[10px] bg-[color:var(--ts-surface-raised)] animate-pulse"
            style={{ width: `${w}%`, animationDelay: `${w * 6}ms` }}
          />
        ))}
      </div>
      <div className="ts-divider w-full my-10" />
      <div className="font-mono-alt text-[11px] text-[color:var(--ts-text-muted)] tracking-widest">
        CLAUDE 4.5 · GOOGLE FACT CHECK · NEWSAPI · GDELT · IMAGE SCRUTINY
      </div>
    </div>
  );
}

function ResultPanel({ result }) {
  const conf = Math.max(0, Math.min(100, Number(result.confidence) || 0));
  // Icon per source key — makes the breakdown scan faster than word matching.
  const SOURCE_ICON = {
    claude: BrainCircuit,
    fact_check: ShieldCheck,
    news_api: Newspaper,
    gdelt: Globe,
  };
  return (
    <div data-testid="result-panel" className="space-y-8">
      {/* Verdict block */}
      <div className="ts-panel p-8 md:p-10">
        <div className="flex items-start justify-between gap-4 mb-8">
          <span className="ts-eyebrow">Verdict</span>
          <span className={`ts-chip ${verdictClass(result.verdict)}`}>
            <VerdictIcon v={result.verdict} className="w-3.5 h-3.5" />
            {result.verdict}
          </span>
        </div>

        {result.corroboration &&
          result.corroboration.has_external_corroboration === false && (
            <div
              data-testid="textual-only-callout"
              className="ts-textual-only-callout mb-8 flex items-start gap-4"
            >
              <AlertOctagon
                size={18}
                strokeWidth={1.5}
                className="shrink-0 text-[color:var(--ts-warn-text)] mt-0.5"
              />
              <div className="min-w-0">
                <div className="font-mono-alt text-[10.5px] tracking-[0.32em] uppercase text-[color:var(--ts-warn-text)]">
                  Textual Analysis Only
                </div>
                <div className="mt-1.5 text-[13px] font-light text-[color:var(--ts-text)] leading-snug max-w-2xl">
                  No external corroboration is available for this article.
                  The verdict rests on textual analysis alone —
                  treat the confidence score with extra scepticism and verify
                  against primary sources before sharing.
                </div>
              </div>
            </div>
          )}

        <div className="flex flex-wrap items-end gap-x-10 gap-y-6 mb-8">
          <div className="flex items-end gap-4">
            <div
              data-testid="confidence-value"
              className="font-serif-display text-6xl md:text-7xl leading-none"
            >
              {conf}
              <span className="text-2xl align-top text-[color:var(--ts-text-muted)] ml-1">
                %
              </span>
            </div>
            <div className="pb-2">
              <div className="ts-eyebrow mb-1">Confidence</div>
              <div className="text-[12px] text-[color:var(--ts-text-muted)] font-light max-w-[180px]">
                Certainty from textual analysis alone.
              </div>
            </div>
          </div>

          {result.corroboration && (
            <div
              data-testid="corroboration-chip"
              className="flex items-end gap-4 pl-10 border-l border-[color:var(--ts-border)]"
            >
              <div
                data-testid="corroboration-score"
                className={`font-serif-display text-6xl md:text-7xl leading-none ${
                  result.corroboration.band === "STRONG"
                    ? "text-[color:var(--ts-success-text)]"
                    : result.corroboration.band === "WEAK"
                      ? "text-[color:var(--ts-danger-text)]"
                      : "text-[color:var(--ts-warn-text)]"
                }`}
              >
                {result.corroboration.score}
              </div>
              <div className="pb-2">
                <div className="ts-eyebrow mb-1">
                  Corroboration ·{" "}
                  <span
                    data-testid="corroboration-band"
                    className={
                      result.corroboration.band === "STRONG"
                        ? "text-[color:var(--ts-success-text)]"
                        : result.corroboration.band === "WEAK"
                          ? "text-[color:var(--ts-danger-text)]"
                          : "text-[color:var(--ts-warn-text)]"
                    }
                  >
                    {result.corroboration.band}
                  </span>
                </div>
                <div className="text-[12px] text-[color:var(--ts-text-muted)] font-light max-w-[220px]">
                  Fused evidence from{" "}
                  {result.corroboration.signals.length} signal
                  {result.corroboration.signals.length === 1 ? "" : "s"} ·{" "}
                  <span
                    className={
                      result.corroboration.verdict_alignment === "AGREES"
                        ? "text-[color:var(--ts-success-text)]"
                        : result.corroboration.verdict_alignment === "CONFLICTS"
                          ? "text-[color:var(--ts-danger-text)]"
                          : "text-[color:var(--ts-text-muted)]"
                    }
                  >
                    {result.corroboration.verdict_alignment.toLowerCase()}
                  </span>{" "}
                  with verdict
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="ts-meter mb-6">
          <span style={{ width: `${conf}%` }} />
        </div>

        {result.corroboration && result.corroboration.consensus && (
          <div
            data-testid="consensus-ribbon"
            className="relative mb-6 overflow-hidden ts-consensus-ribbon"
          >
            <div className="flex items-center gap-4 px-5 py-3.5">
              <span
                className="ts-dot ts-dot-lg shrink-0"
                style={{ background: "var(--ts-accent)" }}
              />
              <div className="flex-1 min-w-0">
                <div className="font-mono-alt text-[10.5px] tracking-[0.32em] uppercase text-[color:var(--ts-accent)]">
                  Strong Consensus
                </div>
                <div className="mt-1 text-[13px] font-light text-[color:var(--ts-text)] leading-snug">
                  {result.corroboration.consensus_sources.length} independent sources —{" "}
                  <span className="font-mono-alt text-[11px] tracking-[0.14em] uppercase text-[color:var(--ts-accent)]">
                    {result.corroboration.consensus_sources
                      .map((k) => k.replace(/_/g, " "))
                      .join(" · ")}
                  </span>{" "}
                  — all agree with the verdict.
                </div>
              </div>
              <Sparkles
                size={16}
                strokeWidth={1.4}
                className="shrink-0 text-[color:var(--ts-accent)]"
              />
            </div>
          </div>
        )}

        {result.corroboration && (
          <details
            data-testid="corroboration-breakdown"
            className="mb-6 group"
          >
            <summary className="cursor-pointer list-none flex items-center gap-2 text-[11px] font-mono-alt tracking-[0.22em] uppercase text-[color:var(--ts-text-muted)] hover:text-[color:var(--ts-text)] transition-colors select-none">
              <ChevronRight
                size={12}
                strokeWidth={1.6}
                className="transition-transform group-open:rotate-90"
              />
              Signal Breakdown · &ldquo;{result.corroboration.note}&rdquo;
            </summary>
            <ul className="mt-4 space-y-2">
              {result.corroboration.signals.map((s, idx) => {
                const dotColor =
                  s.direction > 0
                    ? "var(--ts-success-text)"
                    : s.direction < 0
                      ? "var(--ts-danger-text)"
                      : "var(--ts-text-muted)";
                const SrcIcon = SOURCE_ICON[s.source] || null;
                return (
                  <li
                    key={`${idx}-${s.source}-${s.label}`}
                    data-testid={`signal-row-${s.source}`}
                    className="flex items-baseline gap-3 text-[13px]"
                  >
                    <span
                      className="ts-dot mt-1.5 shrink-0"
                      style={{ background: dotColor }}
                    />
                    <span className="flex items-center gap-2 font-mono-alt text-[10px] tracking-[0.18em] uppercase text-[color:var(--ts-text-muted)] w-[110px] shrink-0">
                      {SrcIcon && (
                        <SrcIcon
                          size={12}
                          strokeWidth={1.5}
                          className="shrink-0 text-[color:var(--ts-accent)]/80"
                          data-testid={`signal-icon-${s.source}`}
                        />
                      )}
                      {s.source.replace(/_/g, " ")}
                    </span>
                    <span className="flex-1 text-[color:var(--ts-text)] font-light">
                      {s.detail}
                      {s.hint && (
                        <span
                          data-testid={`signal-hint-${s.source}`}
                          className="block mt-1 text-[11.5px] font-light italic text-[color:var(--ts-text-muted)] leading-relaxed"
                        >
                          <Info size={10} strokeWidth={1.6} className="inline-block -mt-0.5 mr-1" />
                          {s.hint}
                        </span>
                      )}
                    </span>
                    {s.aligned_with_claude && (
                      <span
                        data-testid={`signal-aligned-${s.source}`}
                        className="font-mono-alt text-[9.5px] tracking-[0.18em] uppercase text-[color:var(--ts-accent)] shrink-0"
                        title="This source agrees with the verdict — its weight is boosted 25%."
                      >
                        aligned
                      </span>
                    )}
                    <span className="font-mono-alt text-[10px] tracking-[0.14em] uppercase text-[color:var(--ts-accent)]">
                      w{s.weight}
                    </span>
                  </li>
                );
              })}
            </ul>
          </details>
        )}

        {result.summary && (
          <p
            data-testid="summary-text"
            className="font-serif-display italic text-xl md:text-2xl leading-snug text-[color:var(--ts-text)]"
          >
            “{result.summary}”
          </p>
        )}

        {result.source_url && (
          <div className="mt-6 pt-6 border-t border-[color:var(--ts-border)]">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="ts-eyebrow mb-2">Original Source</div>
                <a
                  data-testid="source-url-link"
                  href={result.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[13px] font-light text-[color:var(--ts-accent)] hover:text-[color:var(--ts-text)] underline underline-offset-4 decoration-[color:var(--ts-accent-dim)] transition-colors break-all"
                >
                  {result.source_url}
                </a>
              </div>
              <a
                href={result.source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="ts-btn-ghost inline-flex items-center gap-2 shrink-0"
              >
                <ExternalLink size={12} strokeWidth={1.6} />
                View Original
              </a>
            </div>
          </div>
        )}
      </div>

      {/* Category */}
      <div className="ts-panel p-8">
        <SectionTitle icon={Tag}>Classification</SectionTitle>
        <div className="flex flex-wrap items-center gap-3">
          <span data-testid="category-chip" className="ts-chip">
            <span
              className="ts-dot"
              style={{ background: "var(--ts-accent)" }}
            />
            {result.category}
          </span>
          {result.subcategory && (
            <span data-testid="subcategory-chip" className="ts-chip">
              {result.subcategory}
            </span>
          )}
        </div>
      </div>

      {/* Reasoning */}
      <div className="ts-panel p-8">
        <SectionTitle icon={FileText}>Reasoning</SectionTitle>
        <ListBlock items={result.reasoning} testId="reasoning-list" ordered />
      </div>

      {/* Red flags */}
      <div className="ts-panel p-8">
        <SectionTitle icon={Flag}>Red Flags &amp; Cues</SectionTitle>
        {(!result.red_flags || result.red_flags.length === 0) ? (
          <p
            data-testid="red-flags-list-empty"
            className="text-[color:var(--ts-text-muted)] text-sm italic"
          >
            None detected.
          </p>
        ) : (
          <ul data-testid="red-flags-list" className="space-y-6">
            {result.red_flags.map((flag, idx) => {
              const quotes =
                (result.red_flag_evidence && result.red_flag_evidence[idx]) || [];
              return (
                <li
                  key={`${idx}-${flag}`}
                  className="ts-fade-up"
                  style={{ animationDelay: `${idx * 70}ms` }}
                >
                  <div className="flex gap-3">
                    <span className="font-mono-alt text-[11px] text-[color:var(--ts-accent)] mt-1.5 shrink-0">
                      —
                    </span>
                    <div className="flex-1">
                      <div className="text-[15px] leading-relaxed text-[color:var(--ts-text)] font-light">
                        {flag}
                      </div>
                      {quotes.length > 0 && (
                        <div
                          data-testid={`red-flag-quotes-${idx}`}
                          className="mt-3 space-y-2 border-l border-[color:var(--ts-accent-dim)] pl-4"
                        >
                          {quotes.map((q, qi) => (
                            <p
                              key={`${qi}-${q}`}
                              className="font-serif-display italic text-[15px] leading-snug text-[color:var(--ts-text-muted)]"
                            >
                              &ldquo;{q}&rdquo;
                            </p>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Linguistic markers */}
      <div className="ts-panel p-8">
        <SectionTitle icon={TypeIcon}>Linguistic Markers</SectionTitle>
        <div className="flex flex-wrap gap-2" data-testid="markers-list">
          {(result.linguistic_markers || []).length === 0 ? (
            <p className="text-[color:var(--ts-text-muted)] text-sm italic">None detected.</p>
          ) : (
            result.linguistic_markers.map((m, i) => (
              <span key={`${i}-${m}`} className="ts-chip">
                {m}
              </span>
            ))
          )}
        </div>
      </div>

      {/* Evidence Analysis — model reasoning over the external evidence bundle */}
      {result.evidence_analysis && result.evidence_analysis.length > 0 && (
        <div className="ts-panel p-8" data-testid="evidence-analysis-panel">
          <SectionTitle icon={ShieldQuestion}>Evidence Integration</SectionTitle>
          <p className="text-xs text-[color:var(--ts-text-muted)] font-light mb-6 leading-relaxed max-w-lg">
            How the model weighed the external evidence brief (Google Fact Check,
            NewsAPI, GDELT) alongside the article&apos;s own text to reach this
            verdict.
          </p>
          <ListBlock
            items={result.evidence_analysis}
            testId="evidence-analysis-list"
            ordered
          />
        </div>
      )}

      {/* Cross-Reference (auto) — populated during the harmonised classify call */}
      {result.cross_reference && (
        <VerifyPanel data={result.cross_reference} />
      )}

      {/* Recommended checks */}
      <div className="ts-panel p-8">
        <SectionTitle icon={ClipboardList}>Recommended Verification</SectionTitle>
        <ListBlock items={result.recommended_checks} testId="checks-list" ordered />
      </div>

      {/* Suggested Sources / Citations */}
      {result.citations && result.citations.length > 0 && (
        <div className="ts-panel p-8" data-testid="citations-panel">
          <SectionTitle icon={BookOpen}>Sources to Consult</SectionTitle>
          <p className="text-xs text-[color:var(--ts-text-muted)] font-light mb-6 leading-relaxed max-w-lg">
            Authoritative outlets that specialise in the claims made in this
            article. Cross-reference the story against these before drawing any
            conclusion.
          </p>
          <ul data-testid="citations-list" className="space-y-5">
            {result.citations.map((c, idx) => (
              <li
                key={`${idx}-${c.name}`}
                className="ts-fade-up flex gap-3"
                style={{ animationDelay: `${idx * 70}ms` }}
              >
                <span className="font-mono-alt text-[11px] text-[color:var(--ts-accent)] mt-1.5 shrink-0">
                  {String(idx + 1).padStart(2, "0")}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <span className="font-serif-display text-[19px] leading-tight text-[color:var(--ts-text)]">
                      {c.name}
                    </span>
                    <span className="font-mono-alt text-[9.5px] tracking-[0.18em] uppercase text-[color:var(--ts-accent)]">
                      {c.type.replace(/_/g, " ")}
                    </span>
                  </div>
                  <div className="mt-1.5 text-[14px] font-light leading-relaxed text-[color:var(--ts-text-muted)]">
                    {c.why}
                  </div>
                </div>
              </li>
            ))}
          </ul>
          <p className="mt-6 text-[10.5px] font-mono-alt tracking-[0.18em] uppercase text-[color:var(--ts-text-muted)]">
            Note · AI-suggested organisations. Look them up directly rather than
            trusting any linked domain.
          </p>
        </div>
      )}
    </div>
  );
}

// ---------- Cross-Reference Panel ----------
function SourceHeader({ icon: Icon, label, block }) {
  const status = !block
    ? "PENDING"
    : block.available
      ? block.total !== null && block.total !== undefined
        ? `${block.total} MATCHES`
        : "AVAILABLE"
      : "UNAVAILABLE";
  const statusColor = !block
    ? "text-[color:var(--ts-text-muted)]"
    : block.available
      ? "text-[color:var(--ts-accent)]"
      : "text-[color:var(--ts-warn-text)]";
  return (
    <div className="flex items-center justify-between mb-5">
      <div className="flex items-center gap-2">
        <Icon
          size={14}
          strokeWidth={1.4}
          className="text-[color:var(--ts-accent)]"
        />
        <span className="ts-eyebrow">{label}</span>
      </div>
      <span className={`font-mono-alt text-[10px] tracking-[0.22em] uppercase ${statusColor}`}>
        {status}
      </span>
    </div>
  );
}

function UnavailableNote({ reason, keyName }) {
  if (reason && reason.includes("not configured")) {
    return (
      <div className="p-4 border border-dashed border-[color:var(--ts-border)]">
        <div className="font-mono-alt text-[10px] tracking-[0.22em] uppercase text-[color:var(--ts-warn-text)] mb-2">
          Awaiting Key
        </div>
        <p className="text-[13px] font-light text-[color:var(--ts-text-muted)] leading-relaxed">
          Set{" "}
          <span className="font-mono-alt text-[color:var(--ts-text)]">
            {keyName}
          </span>{" "}
          in the backend environment to enable this source.
        </p>
      </div>
    );
  }
  return (
    <p className="text-[13px] font-light italic text-[color:var(--ts-text-muted)] leading-relaxed">
      {reason || "Source returned no data."}
    </p>
  );
}

function FactCheckList({ claims }) {
  if (!claims || claims.length === 0) {
    return (
      <p className="text-[13px] font-light italic text-[color:var(--ts-text-muted)]">
        No matching fact-check reviews were found for this query.
      </p>
    );
  }
  return (
    <ul data-testid="factcheck-list" className="space-y-5">
      {claims.slice(0, 6).map((c, idx) => (
        <li
          key={`${idx}-${c.url || c.text.slice(0, 24)}`}
          className="ts-fade-up"
          style={{ animationDelay: `${idx * 60}ms` }}
        >
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-2">
            {c.rating && (
              <span className="font-mono-alt text-[10px] tracking-[0.18em] uppercase px-2 py-0.5 border border-[color:var(--ts-accent-dim)] text-[color:var(--ts-accent)]">
                {c.rating}
              </span>
            )}
            {c.publisher && (
              <span className="font-mono-alt text-[10px] tracking-[0.18em] uppercase text-[color:var(--ts-text-muted)]">
                {c.publisher}
              </span>
            )}
            {c.review_date && (
              <span className="font-mono-alt text-[10px] tracking-[0.14em] uppercase text-[color:var(--ts-text-muted)]">
                {c.review_date.slice(0, 10)}
              </span>
            )}
          </div>
          <p className="font-serif-display italic text-[16px] leading-snug text-[color:var(--ts-text)]">
            &ldquo;{c.text}&rdquo;
          </p>
          {c.claimant && (
            <p className="text-[12px] font-light text-[color:var(--ts-text-muted)] mt-1">
              — attributed to {c.claimant}
            </p>
          )}
          {c.url && (
            <a
              href={c.url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-mono-alt tracking-[0.16em] uppercase text-[color:var(--ts-accent)] hover:text-[color:var(--ts-text)] transition-colors"
            >
              Read Review <ExternalLink size={10} strokeWidth={1.6} />
            </a>
          )}
        </li>
      ))}
    </ul>
  );
}

function NewsList({ articles }) {
  if (!articles || articles.length === 0) {
    return (
      <p className="text-[13px] font-light italic text-[color:var(--ts-text-muted)]">
        No matching news coverage found.
      </p>
    );
  }
  return (
    <ul data-testid="news-list" className="space-y-4">
      {articles.slice(0, 8).map((a, idx) => (
        <li
          key={`${idx}-${a.url}`}
          className="ts-fade-up"
          style={{ animationDelay: `${idx * 50}ms` }}
        >
          <div className="flex items-baseline gap-3 mb-1">
            <span className="font-mono-alt text-[10px] tracking-[0.18em] uppercase text-[color:var(--ts-accent)]">
              {a.source || "Unknown"}
            </span>
            {a.published_at && (
              <span className="font-mono-alt text-[10px] tracking-[0.14em] uppercase text-[color:var(--ts-text-muted)]">
                {a.published_at.slice(0, 10)}
              </span>
            )}
          </div>
          <a
            href={a.url}
            target="_blank"
            rel="noopener noreferrer"
            className="font-serif-display text-[17px] leading-snug text-[color:var(--ts-text)] hover:text-[color:var(--ts-accent)] transition-colors block"
          >
            {a.title}
          </a>
          {a.description && (
            <p className="text-[13px] font-light text-[color:var(--ts-text-muted)] mt-1 leading-relaxed">
              {a.description}
            </p>
          )}
        </li>
      ))}
    </ul>
  );
}

function GdeltList({ block }) {
  const { articles, top_domains: topDomains, total } = block;
  return (
    <div>
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="ts-panel-raised p-4">
          <div className="ts-eyebrow mb-2">Total Coverage</div>
          <div className="font-serif-display text-3xl leading-none">{total ?? 0}</div>
          <div className="text-[11px] font-mono-alt tracking-[0.14em] uppercase text-[color:var(--ts-text-muted)] mt-2">
            Global articles indexed
          </div>
        </div>
        <div className="ts-panel-raised p-4">
          <div className="ts-eyebrow mb-2">Top Outlets</div>
          {topDomains && topDomains.length > 0 ? (
            <ul className="space-y-1.5">
              {topDomains.slice(0, 4).map((d) => (
                <li
                  key={d.domain}
                  className="flex items-baseline justify-between gap-3"
                >
                  <span className="text-[13px] font-light text-[color:var(--ts-text)] truncate">
                    {d.domain}
                  </span>
                  <span className="font-mono-alt text-[10px] tracking-[0.14em] text-[color:var(--ts-accent)]">
                    ×{d.count}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-[12px] italic text-[color:var(--ts-text-muted)]">
              None found.
            </p>
          )}
        </div>
      </div>
      {(!articles || articles.length === 0) ? (
        <p className="text-[13px] font-light italic text-[color:var(--ts-text-muted)]">
          No articles indexed for this query.
        </p>
      ) : (
        <ul data-testid="gdelt-list" className="space-y-3">
          {articles.slice(0, 6).map((a, idx) => (
            <li
              key={`${idx}-${a.url}`}
              className="ts-fade-up flex gap-3"
              style={{ animationDelay: `${idx * 40}ms` }}
            >
              <span className="font-mono-alt text-[10.5px] text-[color:var(--ts-accent)] mt-1 shrink-0 tracking-[0.14em] uppercase">
                {a.language || "—"}
              </span>
              <div className="flex-1 min-w-0">
                <a
                  href={a.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-serif-display italic text-[15px] leading-snug text-[color:var(--ts-text)] hover:text-[color:var(--ts-accent)] transition-colors block"
                >
                  {a.title || a.url}
                </a>
                <div className="text-[10.5px] font-mono-alt tracking-[0.14em] uppercase text-[color:var(--ts-text-muted)] mt-1">
                  {a.domain} · {a.country || "Global"}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function VerifyPanel({ data }) {
  const [openSection, setOpenSection] = useState("fact_check");

  const renderBody = (id) => {
    if (id === "fact_check") {
      return data.fact_check.available ? (
        <FactCheckList claims={data.fact_check.claims} />
      ) : (
        <UnavailableNote
          reason={data.fact_check.reason}
          keyName="GOOGLE_FACT_CHECK_API_KEY"
        />
      );
    }
    if (id === "news_api") {
      return data.news_api.available ? (
        <NewsList articles={data.news_api.articles} />
      ) : (
        <UnavailableNote reason={data.news_api.reason} keyName="NEWSAPI_KEY" />
      );
    }
    if (id === "gdelt") {
      return data.gdelt.available ? (
        <GdeltList block={data.gdelt} />
      ) : (
        <UnavailableNote reason={data.gdelt.reason} keyName={null} />
      );
    }
    return null;
  };

  const sections = [
    { id: "fact_check", label: "Google Fact Check", icon: ShieldQuestion, block: data.fact_check },
    { id: "news_api", label: "NewsAPI Coverage", icon: Newspaper, block: data.news_api },
    { id: "gdelt", label: "GDELT Global", icon: Globe, block: data.gdelt },
  ];

  return (
    <div data-testid="verify-panel" className="ts-panel p-8">
      <SectionTitle icon={Search}>Cross-Reference · External Evidence</SectionTitle>
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 mb-6">
        <span className="font-mono-alt text-[10px] tracking-[0.18em] uppercase text-[color:var(--ts-text-muted)]">
          Query
        </span>
        <span className="font-serif-display italic text-[15px] leading-snug text-[color:var(--ts-text)] flex-1 min-w-0 break-words">
          &ldquo;{data.query}&rdquo;
        </span>
      </div>

      <div className="space-y-3">
        {sections.map(({ id, label, icon: Icon, block }) => {
          const isOpen = openSection === id;
          return (
            <div
              key={id}
              className="ts-panel"
              data-testid={`verify-section-${id}`}
            >
              <button
                type="button"
                onClick={() => setOpenSection(isOpen ? null : id)}
                className="w-full flex items-center justify-between px-6 py-5 text-left transition-colors hover:bg-[color:var(--ts-surface-raised)]"
                data-testid={`verify-toggle-${id}`}
              >
                <div className="flex-1">
                  <SourceHeader icon={Icon} label={label} block={block} />
                </div>
                {isOpen ? (
                  <ChevronDown
                    size={14}
                    strokeWidth={1.6}
                    className="text-[color:var(--ts-text-muted)] ml-4"
                  />
                ) : (
                  <ChevronRight
                    size={14}
                    strokeWidth={1.6}
                    className="text-[color:var(--ts-text-muted)] ml-4"
                  />
                )}
              </button>
              {isOpen && (
                <div className="px-6 pb-6 pt-1 border-t border-[color:var(--ts-border)]">
                  <div className="pt-5">{renderBody(id)}</div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <p className="mt-6 text-[10.5px] font-mono-alt tracking-[0.18em] uppercase text-[color:var(--ts-text-muted)]">
        Note · Cross-reference is an aid, not a verdict. Corroboration by mainstream
        outlets or fact-checkers strengthens or weakens the AI verdict.
      </p>
    </div>
  );
}

// ---------- Scan the Article (camera viewfinder + snap) ----------
// Resize a source dataURL / video-frame down to a manageable payload for the
// vision call. Max 1600px on the longer edge, JPEG q0.85 — sharp enough for OCR
// yet fast on flaky mobile networks.
const MAX_SCAN_EDGE = 1600;
const captureFromVideo = (video) => {
  const vw = video.videoWidth || 1280;
  const vh = video.videoHeight || 720;
  const scale = Math.min(1, MAX_SCAN_EDGE / Math.max(vw, vh));
  const w = Math.round(vw * scale);
  const h = Math.round(vh * scale);
  const off = document.createElement("canvas");
  off.width = w;
  off.height = h;
  const ctx = off.getContext("2d");
  ctx.drawImage(video, 0, 0, w, h);
  return off.toDataURL("image/jpeg", 0.85);
};

function ScanArticle({ onQuotaError, applyUsage }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const detectorTimerRef = useRef(null);
  const detectorSampleRef = useRef({ pos: 0, neg: 0 });
  const [status, setStatus] = useState("idle"); // idle | starting | live | denied | unsupported
  const [errorMsg, setErrorMsg] = useState("");
  const [docDetected, setDocDetected] = useState(false);
  // Multi-page snapshots: array of dataURLs (order matters — page 1 first)
  const [pages, setPages] = useState([]);
  const MAX_PAGES = 4;

  // OCR + classification state
  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrText, setOcrText] = useState("");
  const [ocrNote, setOcrNote] = useState("");
  const [ocrPages, setOcrPages] = useState([]); // per-page word counts from backend
  const [classifying, setClassifying] = useState(false);
  const [scanResult, setScanResult] = useState(null);

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const resetAll = () => {
    setPages([]);
    setOcrText("");
    setOcrNote("");
    setOcrPages([]);
    setScanResult(null);
  };

  // Reset only the OCR / classification derived state (keeps captured pages).
  const invalidateOcr = () => {
    setOcrText("");
    setOcrNote("");
    setOcrPages([]);
    setScanResult(null);
  };

  const startCamera = async () => {
    setErrorMsg("");
    invalidateOcr();
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setStatus("unsupported");
      setErrorMsg("This browser does not expose a camera API. Try Chrome, Safari, or Firefox on a device with a camera.");
      return;
    }
    if (window.location.protocol !== "https:" && window.location.hostname !== "localhost") {
      setStatus("unsupported");
      setErrorMsg("Camera access requires an https:// connection.");
      return;
    }
    setStatus("starting");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: "environment" },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setStatus("live");
    } catch (e) {
      const name = e && e.name;
      if (name === "NotAllowedError" || name === "PermissionDeniedError") {
        setStatus("denied");
        setErrorMsg("Camera permission was denied. Allow access in your browser settings and try again.");
      } else if (name === "NotFoundError" || name === "OverconstrainedError") {
        setStatus("unsupported");
        setErrorMsg("No compatible camera was found on this device.");
      } else {
        setStatus("unsupported");
        setErrorMsg((e && e.message) || "Camera failed to start.");
      }
    }
  };

  const snapPhoto = () => {
    const video = videoRef.current;
    if (!video || video.readyState < 2) {
      toast.error("Camera is not ready yet.");
      return;
    }
    if (pages.length >= MAX_PAGES) {
      toast.error(`Maximum ${MAX_PAGES} pages per scan.`);
      return;
    }
    const dataUrl = captureFromVideo(video);
    setPages((p) => [...p, dataUrl]);
    invalidateOcr();
    toast.success(`Page ${pages.length + 1} captured.`);
  };

  const onUploadFile = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length === 0) return;
    const slotsLeft = MAX_PAGES - pages.length;
    if (slotsLeft <= 0) {
      toast.error(`Maximum ${MAX_PAGES} pages per scan.`);
      return;
    }
    const toLoad = files.slice(0, slotsLeft);
    if (files.length > slotsLeft) {
      toast(`Only ${slotsLeft} more page${slotsLeft === 1 ? "" : "s"} added (max ${MAX_PAGES}).`);
    }
    const loaded = [];
    for (const file of toLoad) {
      if (!/^image\/(jpeg|jpg|png|webp)$/i.test(file.type)) {
        toast.error(`Skipped ${file.name}: unsupported format.`);
        continue;
      }
      if (file.size > 12 * 1024 * 1024) {
        toast.error(`Skipped ${file.name}: over 12 MB.`);
        continue;
      }
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const img = new Image();
          img.onload = () => {
            const scale = Math.min(1, MAX_SCAN_EDGE / Math.max(img.width, img.height));
            const w = Math.round(img.width * scale);
            const h = Math.round(img.height * scale);
            const off = document.createElement("canvas");
            off.width = w;
            off.height = h;
            off.getContext("2d").drawImage(img, 0, 0, w, h);
            resolve(off.toDataURL("image/jpeg", 0.85));
          };
          img.onerror = () => reject(new Error("decode"));
          img.src = reader.result;
        };
        reader.onerror = () => reject(new Error("read"));
        reader.readAsDataURL(file);
      }).catch(() => null);
      if (dataUrl) loaded.push(dataUrl);
    }
    if (loaded.length > 0) {
      setPages((p) => [...p, ...loaded]);
      invalidateOcr();
      toast.success(`Added ${loaded.length} page${loaded.length === 1 ? "" : "s"}.`);
    }
  };

  const removePage = (idx) => {
    setPages((p) => p.filter((_, i) => i !== idx));
    invalidateOcr();
  };

  const movePage = (idx, dir) => {
    setPages((p) => {
      const nxt = [...p];
      const j = idx + dir;
      if (j < 0 || j >= nxt.length) return p;
      [nxt[idx], nxt[j]] = [nxt[j], nxt[idx]];
      return nxt;
    });
    invalidateOcr();
  };

  const downloadPage = (idx) => {
    const src = pages[idx];
    if (!src) return;
    const a = document.createElement("a");
    a.href = src;
    a.download = `fake-news-scan-p${idx + 1}-${Date.now()}.jpg`;
    a.click();
  };

  const extractText = async () => {
    if (pages.length === 0) return;
    setOcrLoading(true);
    setOcrNote("");
    setOcrPages([]);
    setScanResult(null);

    // One attempt = one POST /api/scan-image. On a Cloudflare 524 (edge
    // timed out waiting for the origin), 502/504, or a raw network error we
    // silently retry ONCE with a slight delay — those are typically caused
    // by cold-start / transient network glitches on mobile.
    const doAttempt = async () => {
      // Give browsers headroom over the backend's 45s/page timeout so we
      // never abort a valid response prematurely.
      return axios.post(
        `${API}/scan-image`,
        { images_base64: pages },
        { timeout: 180_000 },
      );
    };

    const isTransient = (err) => {
      const s = err?.response?.status;
      if (s === 502 || s === 503 || s === 504 || s === 524) return true;
      // No response at all → network layer / edge dropped the connection.
      if (!err?.response && err?.code !== "ERR_CANCELED") return true;
      return false;
    };

    let res;
    try {
      res = await doAttempt();
    } catch (e) {
      if (isTransient(e)) {
        toast("Server was slow — retrying once…");
        await new Promise((r) => setTimeout(r, 1500));
        try {
          res = await doAttempt();
        } catch (e2) {
          setOcrLoading(false);
          if (e2?.response?.status === 402 && onQuotaError) {
            onQuotaError(e2.response.data?.detail);
            return;
          }
          toast.error(
            "The OCR service is taking longer than expected. Try again with fewer or sharper pages.",
          );
          return;
        }
      } else if (e?.response?.status === 402 && onQuotaError) {
        setOcrLoading(false);
        onQuotaError(e.response.data?.detail);
        return;
      } else {
        setOcrLoading(false);
        const msg = e?.response?.data?.detail || e.message || "OCR failed.";
        toast.error(String(msg));
        return;
      }
    }

    const data = res.data || {};
    setOcrText(data.text || "");
    setOcrNote(data.note || "");
    setOcrPages(Array.isArray(data.pages) ? data.pages : []);
    if (data.usage && applyUsage) applyUsage(data.usage);
    if (!data.text || data.word_count < 5) {
      toast.error(data.note || "No readable text was found.");
    } else if (data.usage?.cached) {
      toast.success("Served from cache · no scan spent.");
    } else if (pages.length === 1) {
      toast.success(`Extracted ${data.word_count} words.`);
    } else {
      toast.success(`Extracted ${data.word_count} words across ${pages.length} pages.`);
    }
    setOcrLoading(false);
  };

  const classifyScanned = async () => {
    const t = ocrText.trim();
    if (t.length < 20) {
      toast.error("Extracted text is too short. Please capture a clearer photo.");
      return;
    }
    setClassifying(true);
    setScanResult(null);
    try {
      const res = await axios.post(`${API}/classify`, { text: t });
      setScanResult(res.data);
      if (res.data.usage && applyUsage) applyUsage(res.data.usage);
      if (res.data.usage?.cached) {
        toast.success("Served from cache · no scan spent.");
      } else {
        toast.success("Analysis complete.");
      }
    } catch (e) {
      if (e?.response?.status === 402 && onQuotaError) {
        onQuotaError(e.response.data?.detail);
      } else {
        const msg = e?.response?.data?.detail || e.message || "Analysis failed.";
        toast.error(String(msg));
      }
    } finally {
      setClassifying(false);
    }
  };

  // Lightweight in-browser document-detection heuristic.
  // Every 400ms while the camera is live: pull a 64x64 canvas frame, compute
  // (a) center-vs-border brightness delta (paper reflects more light than the
  // surrounding scene) and (b) center standard deviation (text/lines create
  // variance in an otherwise flat page). Both above threshold → detected.
  // Two consecutive positive samples flip to green; two negatives back to
  // white — prevents flicker on shaky hands.
  useEffect(() => {
    if (status !== "live") {
      setDocDetected(false);
      detectorSampleRef.current = { pos: 0, neg: 0 };
      if (detectorTimerRef.current) {
        clearInterval(detectorTimerRef.current);
        detectorTimerRef.current = null;
      }
      return undefined;
    }
    const canvas = document.createElement("canvas");
    canvas.width = 64;
    canvas.height = 64;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    const sample = () => {
      const video = videoRef.current;
      if (!video || video.readyState < 2) return;
      try {
        ctx.drawImage(video, 0, 0, 64, 64);
        const data = ctx.getImageData(0, 0, 64, 64).data;
        const gray = new Float32Array(64 * 64);
        let centerSum = 0, centerCount = 0;
        let borderSum = 0, borderCount = 0;
        for (let y = 0; y < 64; y += 1) {
          for (let x = 0; x < 64; x += 1) {
            const i = (y * 64 + x) * 4;
            const g = data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
            gray[y * 64 + x] = g;
            if (x >= 16 && x < 48 && y >= 16 && y < 48) {
              centerSum += g;
              centerCount += 1;
            } else if (x < 8 || x >= 56 || y < 8 || y >= 56) {
              borderSum += g;
              borderCount += 1;
            }
          }
        }
        const centerAvg = centerSum / centerCount;
        const borderAvg = borderSum / borderCount;
        let variance = 0;
        for (let y = 16; y < 48; y += 1) {
          for (let x = 16; x < 48; x += 1) {
            const diff = gray[y * 64 + x] - centerAvg;
            variance += diff * diff;
          }
        }
        variance /= centerCount;
        const stdDev = Math.sqrt(variance);
        // Thresholds tuned empirically for typical phone camera of a printed
        // page in indoor lighting — paper is ~12+ luminance above scene and
        // text/lines yield stdDev ~22-40.
        const detected = centerAvg - borderAvg > 12 && stdDev > 22;
        if (detected) {
          detectorSampleRef.current.neg = 0;
          detectorSampleRef.current.pos += 1;
          if (detectorSampleRef.current.pos >= 2) setDocDetected(true);
        } else {
          detectorSampleRef.current.pos = 0;
          detectorSampleRef.current.neg += 1;
          if (detectorSampleRef.current.neg >= 2) setDocDetected(false);
        }
      } catch {
        // Canvas can throw SecurityError if the video track is tainted; just
        // skip this tick — the next one will retry.
      }
    };
    detectorTimerRef.current = setInterval(sample, 400);
    return () => {
      if (detectorTimerRef.current) {
        clearInterval(detectorTimerRef.current);
        detectorTimerRef.current = null;
      }
    };
  }, [status]);

  useEffect(() => {
    const stream = streamRef;
    return () => {
      if (stream.current) {
        stream.current.getTracks().forEach((t) => t.stop());
        stream.current = null;
      }
    };
  }, []);

  const hasReadableText = ocrText.trim().length >= 20;
  const canAddMore = pages.length < MAX_PAGES;

  return (
    <main
      data-testid="scan-view"
      className="max-w-6xl mx-auto px-6 md:px-10 pt-16 md:pt-20 pb-24"
    >
      <span className="ts-eyebrow">Camera Capture</span>
      <h1 className="font-serif-display text-4xl sm:text-5xl leading-[1.05] tracking-tight mt-6 max-w-3xl">
        Scan the article <span className="italic text-[color:var(--ts-accent)]">in front of you</span>.
      </h1>
      <p className="mt-6 max-w-2xl text-[color:var(--ts-text-muted)] font-light leading-relaxed text-[15px]">
        Point your camera at a printed article, headline, or screen — or snap multiple pages of a
        long story.
      </p>

      <div className="mt-12 flex flex-col items-center gap-10">
        {/* Viewfinder — 80% of viewport width, square, centered */}
        <div className="w-full max-w-6xl">
          <div className="flex items-baseline justify-between mb-4 px-2 md:px-6">
            <span className="ts-eyebrow">Viewfinder</span>
            <span
              data-testid="page-count"
              className="font-mono-alt text-[10.5px] tracking-[0.18em] uppercase text-[color:var(--ts-text-muted)]"
            >
              {pages.length} / {MAX_PAGES} pages
            </span>
          </div>
          <div
            data-testid="viewfinder"
            className="relative ts-panel overflow-hidden bg-black mx-auto w-[80vw] h-[80vw] max-w-[720px] max-h-[720px]"
          >
            <video
              ref={videoRef}
              data-testid="camera-video"
              playsInline
              muted
              autoPlay
              className={`w-full h-full object-cover ${status === "live" ? "" : "opacity-0"}`}
            />
            {status === "live" && (
              <>
                {/* L-shaped corner markers — turn from white to green when a
                    document is detected inside the frame. */}
                {[
                  "top-4 left-4 border-t-2 border-l-2",
                  "top-4 right-4 border-t-2 border-r-2",
                  "bottom-4 left-4 border-b-2 border-l-2",
                  "bottom-4 right-4 border-b-2 border-r-2",
                ].map((pos) => (
                  <div
                    key={pos}
                    data-testid={docDetected ? "corner-marker-green" : "corner-marker-white"}
                    className={`pointer-events-none absolute w-9 h-9 md:w-12 md:h-12 transition-colors duration-300 ${pos} ${
                      docDetected
                        ? "border-[color:var(--ts-success-text)]"
                        : "border-white"
                    }`}
                  />
                ))}
                <div className="absolute top-3 left-1/2 -translate-x-1/2 flex items-center gap-2 font-mono-alt text-[10px] tracking-[0.22em] uppercase">
                  <span
                    className={`w-1.5 h-1.5 rounded-full ${
                      docDetected ? "bg-[color:var(--ts-success-text)]" : "bg-white"
                    }`}
                  />
                  <span
                    data-testid="detection-status"
                    className={
                      docDetected
                        ? "text-[color:var(--ts-success-text)]"
                        : "text-white/80"
                    }
                  >
                    {docDetected ? "Document detected" : "Looking for a document…"}
                  </span>
                </div>
                {pages.length > 0 && (
                  <div className="absolute bottom-3 right-3 font-mono-alt text-[10px] tracking-[0.22em] uppercase text-[color:var(--ts-accent)] px-2 py-1 bg-black/60">
                    NEXT · PAGE {pages.length + 1}
                  </div>
                )}
              </>
            )}
            {status !== "live" && (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
                {status === "starting" ? (
                  <>
                    <Aperture size={28} strokeWidth={1.4} className="text-[color:var(--ts-accent)] animate-pulse" />
                    <p className="mt-4 font-mono-alt text-[11px] tracking-[0.22em] uppercase text-[color:var(--ts-text-muted)]">Requesting camera…</p>
                  </>
                ) : status === "denied" || status === "unsupported" ? (
                  <>
                    <CameraOff size={28} strokeWidth={1.4} className="text-[color:var(--ts-danger-text)]" />
                    <p data-testid="camera-error" className="mt-4 text-[14px] font-light text-[color:var(--ts-text)] max-w-sm">{errorMsg}</p>
                    <p className="mt-3 text-[11px] font-mono-alt tracking-[0.22em] uppercase text-[color:var(--ts-text-muted)]">
                      Or upload photos instead
                    </p>
                  </>
                ) : (
                  <>
                    <Camera size={28} strokeWidth={1.4} className="text-[color:var(--ts-text-muted)]" />
                    <p className="mt-4 font-mono-alt text-[11px] tracking-[0.22em] uppercase text-[color:var(--ts-text-muted)]">
                      {pages.length === 0 ? "Camera off · press Start" : "Camera off · add more pages or extract"}
                    </p>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Page strip — sits directly below the viewfinder */}
          {pages.length > 0 && (
            <div className="mt-6 max-w-6xl mx-auto px-2 md:px-6" data-testid="page-strip">
              <div className="ts-eyebrow mb-3">Pages · in reading order</div>
              <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1">
                {pages.map((src, idx) => (
                  <div
                    key={`page-${idx}-${src.slice(-24)}`}
                    data-testid={`page-thumb-${idx}`}
                    className="relative shrink-0 w-32 group"
                  >
                    <div className="ts-panel overflow-hidden aspect-[3/4] bg-black">
                      <img
                        src={src}
                        alt={`Page ${idx + 1}`}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute top-1.5 left-1.5 font-mono-alt text-[9.5px] tracking-[0.22em] uppercase text-[color:var(--ts-accent)] bg-black/70 px-1.5 py-0.5">
                        P{idx + 1}
                      </div>
                    </div>
                    <div className="mt-2 flex items-center justify-between gap-1">
                      <button
                        type="button"
                        onClick={() => movePage(idx, -1)}
                        disabled={idx === 0 || ocrLoading || classifying}
                        data-testid={`page-move-left-${idx}`}
                        className="ts-btn-ghost !px-2 !py-1 text-[10px]"
                        aria-label={`Move page ${idx + 1} left`}
                      >
                        ←
                      </button>
                      <button
                        type="button"
                        onClick={() => downloadPage(idx)}
                        data-testid={`page-download-${idx}`}
                        className="ts-btn-ghost !px-2 !py-1 text-[10px]"
                        aria-label={`Download page ${idx + 1}`}
                      >
                        <Download size={10} strokeWidth={1.6} />
                      </button>
                      <button
                        type="button"
                        onClick={() => removePage(idx)}
                        disabled={ocrLoading || classifying}
                        data-testid={`page-remove-${idx}`}
                        className="ts-btn-ghost !px-2 !py-1 text-[10px] text-[color:var(--ts-danger-text)]"
                        aria-label={`Remove page ${idx + 1}`}
                      >
                        <Trash2 size={10} strokeWidth={1.6} />
                      </button>
                      <button
                        type="button"
                        onClick={() => movePage(idx, 1)}
                        disabled={idx === pages.length - 1 || ocrLoading || classifying}
                        data-testid={`page-move-right-${idx}`}
                        className="ts-btn-ghost !px-2 !py-1 text-[10px]"
                        aria-label={`Move page ${idx + 1} right`}
                      >
                        →
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Controls — sit below the viewfinder as a centered card. */}
        <div className="w-full max-w-2xl space-y-4">
          <div className="ts-eyebrow text-center">Controls</div>

          {status !== "live" && (
            <button
              data-testid="start-camera-button"
              type="button"
              onClick={startCamera}
              disabled={!canAddMore || ocrLoading || classifying}
              className="ts-btn-primary inline-flex items-center gap-2 w-full justify-center"
            >
              <Camera size={13} strokeWidth={1.6} />
              {status === "starting" ? "Requesting…" : (pages.length === 0 ? "Start Camera" : "Add Page · Camera")}
            </button>
          )}

          {status === "live" && (
            <>
              <button
                data-testid="snap-photo-button"
                type="button"
                onClick={snapPhoto}
                disabled={!canAddMore}
                className="ts-btn-primary inline-flex items-center gap-2 w-full justify-center"
              >
                <Aperture size={13} strokeWidth={1.6} />
                {pages.length === 0 ? "Snap Photo" : `Snap Page ${pages.length + 1}`}
              </button>
              <button
                data-testid="stop-camera-button"
                type="button"
                onClick={() => { stopStream(); setStatus("idle"); }}
                className="ts-btn-ghost inline-flex items-center gap-2 w-full justify-center"
              >
                <CameraOff size={12} strokeWidth={1.6} />
                Stop Camera
              </button>
            </>
          )}

          <label
            data-testid="upload-photo-label"
            className={`ts-btn-ghost inline-flex items-center gap-2 w-full justify-center cursor-pointer ${
              !canAddMore || ocrLoading || classifying ? "opacity-40 pointer-events-none" : ""
            }`}
          >
            <Upload size={12} strokeWidth={1.6} />
            {pages.length === 0 ? "Upload a photo" : "Upload more pages"}
            <input
              data-testid="upload-photo-input"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              className="hidden"
              onChange={onUploadFile}
            />
          </label>

          {pages.length > 0 && (
            <>
              <div className="ts-divider" />
              <button
                data-testid="extract-text-button"
                type="button"
                onClick={extractText}
                disabled={ocrLoading || classifying}
                className={`inline-flex items-center gap-2 w-full justify-center ${
                  hasReadableText ? "ts-btn-ghost" : "ts-btn-primary"
                }`}
              >
                {ocrLoading ? (
                  <Loader2 size={13} strokeWidth={1.8} className="animate-spin" />
                ) : (
                  <ScanLine size={13} strokeWidth={1.6} />
                )}
                {ocrLoading
                  ? `Reading ${pages.length} page${pages.length === 1 ? "" : "s"}…`
                  : (hasReadableText ? "Re-extract Text" : `Extract Text · ${pages.length} page${pages.length === 1 ? "" : "s"}`)}
              </button>
              {hasReadableText && (
                <button
                  data-testid="classify-scanned-button"
                  type="button"
                  onClick={classifyScanned}
                  disabled={classifying || ocrLoading}
                  className="ts-btn-primary inline-flex items-center gap-2 w-full justify-center"
                >
                  {classifying ? (
                    <Loader2 size={13} strokeWidth={1.8} className="animate-spin" />
                  ) : (
                    <Sparkles size={13} strokeWidth={1.6} />
                  )}
                  {classifying ? "Analyzing…" : "Analyze Article"}
                </button>
              )}
              <button
                data-testid="reset-scan-button"
                type="button"
                onClick={resetAll}
                disabled={ocrLoading || classifying}
                className="ts-btn-ghost inline-flex items-center gap-2 w-full justify-center"
              >
                <RefreshCw size={12} strokeWidth={1.6} />
                Reset scan
              </button>
            </>
          )}

          <p className="text-[11px] font-light text-[color:var(--ts-text-muted)] leading-relaxed pt-4 border-t border-[color:var(--ts-border)]">
            Max {MAX_PAGES} pages per scan.
          </p>
        </div>
      </div>

      {/* Extracted text preview + edit */}
      {(pages.length > 0 && (ocrLoading || ocrText || ocrNote)) && (
        <div className="mt-14" data-testid="ocr-panel">
          <div className="ts-divider mb-8" />
          <div className="flex items-center justify-between mb-4">
            <span className="ts-eyebrow">Extracted Text</span>
            {ocrText && (
              <span className="font-mono-alt text-[10.5px] tracking-[0.18em] uppercase text-[color:var(--ts-text-muted)]">
                <span data-testid="ocr-word-count">{wordCount(ocrText)}</span> words · editable
              </span>
            )}
          </div>
          {ocrLoading ? (
            <div className="ts-panel p-8 ts-tracing">
              <p className="font-mono-alt text-[11px] tracking-[0.22em] uppercase text-[color:var(--ts-text-muted)] mb-4">
                Vision analysis · reading {pages.length} page{pages.length === 1 ? "" : "s"} in order
              </p>
              <div className="space-y-4">
                {[80, 60, 90, 45, 70].map((w) => (
                  <div
                    key={`ocr-sk-${w}`}
                    className="h-[10px] bg-[color:var(--ts-surface-raised)] animate-pulse"
                    style={{ width: `${w}%`, animationDelay: `${w * 6}ms` }}
                  />
                ))}
              </div>
            </div>
          ) : (
            <>
              <textarea
                data-testid="ocr-textarea"
                className="ts-textarea"
                value={ocrText}
                onChange={(e) => setOcrText(e.target.value)}
                placeholder="Extracted article text will appear here. You can edit before analysing."
                spellCheck={false}
              />
              {ocrPages.length > 1 && (
                <div
                  data-testid="ocr-page-breakdown"
                  className="mt-3 flex flex-wrap gap-2 font-mono-alt text-[10px] tracking-[0.18em] uppercase text-[color:var(--ts-text-muted)]"
                >
                  {ocrPages.map((p) => (
                    <span
                      key={`ocr-p-${p.index}`}
                      className="ts-chip !py-1 !px-2 !text-[10px]"
                    >
                      P{p.index + 1} · {p.word_count} w
                    </span>
                  ))}
                </div>
              )}
              {ocrNote && (
                <p
                  data-testid="ocr-note"
                  className="mt-4 text-[13px] font-light italic text-[color:var(--ts-warn-text)] leading-relaxed"
                >
                  {ocrNote}
                </p>
              )}
            </>
          )}
        </div>
      )}

      {/* Classification result */}
      {(classifying || scanResult) && (
        <div className="mt-14" data-testid="scan-result-panel">
          <div className="ts-divider mb-8" />
          <div className="flex items-center justify-between mb-4">
            <span className="ts-eyebrow">Analysis</span>
            {scanResult && (
              <span
                data-testid="scan-verdict-badge"
                className={`ts-chip ${verdictClass(scanResult.verdict)}`}
              >
                <VerdictIcon v={scanResult.verdict} className="w-3 h-3" />
                {scanResult.verdict}
              </span>
            )}
          </div>
          {classifying ? <LoadingState /> : <ResultPanel result={scanResult} />}
        </div>
      )}
    </main>
  );
}


function AppShell() {
  const { user, applyUsage } = useAuth();
  const [activeTab, setActiveTab] = useState("detector");
  const [text, setText] = useState("");
  const [url, setUrl] = useState("");
  const [fetching, setFetching] = useState(false);
  const [source, setSource] = useState(null); // {url,title,site,author,date,hero_image}
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [showUpgrade, setShowUpgrade] = useState({ open: false, reason: "manual" });
  const [showSignInWall, setShowSignInWall] = useState(false);

  const handleQuotaError = useCallback((detail) => {
    const code = (detail && typeof detail === "object") ? detail.code : null;
    if (code === "anon_quota_exhausted") {
      setShowSignInWall(true);
    } else if (code === "free_quota_exhausted") {
      setShowUpgrade({ open: true, reason: "free_quota_exhausted" });
    } else {
      toast.error(String((detail && detail.message) || detail || "Analysis failed."));
    }
  }, []);

  const words = useMemo(() => wordCount(text), [text, wordCount]);
  const chars = text.length;
  const canAnalyze = chars >= 20 && !loading;

  const analyze = async () => {
    if (!canAnalyze) {
      toast.error("Please paste at least 20 characters of article text.");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const payload = { text };
      if (source && source.url) payload.source_url = source.url;
      // Auto-attach the hero image (og:image) so the model can scrutinise it via
      // URL/domain/filename heuristics — no manual field needed.
      if (source && source.hero_image) payload.image_url = source.hero_image;
      const res = await axios.post(`${API}/classify`, payload);
      setResult(res.data);
      if (res.data.usage) applyUsage(res.data.usage);
      if (res.data.usage?.cached) {
        toast.success("Served from cache · no scan spent.");
      } else {
        toast.success("Analysis complete.");
      }
    } catch (e) {
      if (e?.response?.status === 402) {
        handleQuotaError(e.response.data?.detail);
      } else {
        const msg = e?.response?.data?.detail || e.message || "Analysis failed.";
        toast.error(String(msg));
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchFromUrl = async () => {
    const trimmed = url.trim();
    if (!trimmed) {
      toast.error("Paste a URL first.");
      return;
    }
    if (!/^https?:\/\//i.test(trimmed)) {
      toast.error("URL must start with http:// or https://");
      return;
    }
    setFetching(true);
    try {
      const res = await axios.post(`${API}/fetch-url`, { url: trimmed });
      const d = res.data;
      setText(d.text);
      setSource({
        url: d.url,
        title: d.title,
        site: d.site,
        author: d.author,
        date: d.date,
        hero_image: d.hero_image,
      });
      setResult(null);
      toast.success(
        d.title ? `Fetched · ${d.title.slice(0, 60)}` : "Article text fetched."
      );
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Fetch failed.";
      toast.error(String(msg));
    } finally {
      setFetching(false);
    }
  };

  const clearAll = () => {
    setText("");
    setUrl("");
    setSource(null);
    setResult(null);
  };

  const loadSample = () => {
    setText(SAMPLE);
    setSource(null);
    setResult(null);
  };

  const copyResult = async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(result, null, 2));
      toast.success("Report copied to clipboard.");
    } catch {
      toast.error("Copy failed.");
    }
  };

  // Render exactly one of: loading skeleton, populated result, or empty state.
  const renderAnalysisPane = () => {
    if (loading) return <LoadingState />;
    if (result) return <ResultPanel result={result} />;
    return <EmptyState />;
  };

  return (
    <div className="ts-app">
      <Toaster
        theme="dark"
        position="bottom-right"
        toastOptions={TOAST_OPTIONS}
      />

      {/* Header */}
      <header data-testid="app-header" className="border-b border-[color:var(--ts-border)]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-6 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <ScanLine
              size={18}
              strokeWidth={1.4}
              className="text-[color:var(--ts-accent)]"
            />
            <div className="leading-none min-w-0">
              <div className="font-serif-display text-2xl tracking-tight truncate">
                News{" "}
                <span className="italic text-[color:var(--ts-accent)]">
                  Classifier
                </span>
              </div>
              <div className="font-mono-alt text-[10px] tracking-[0.28em] text-[color:var(--ts-text-muted)] mt-1">
                NEWS · CLASSIFIER
              </div>
            </div>
          </div>
          <div className="flex items-center gap-5">
            <div className="hidden md:block">
              <UsageChip onOpenUpgrade={() => setShowUpgrade({ open: true, reason: "manual" })} />
            </div>
            {user ? (
              <UserMenu onOpenUpgrade={() => setShowUpgrade({ open: true, reason: "manual" })} />
            ) : (
              <LoginButton variant="ghost" label="Sign up" />
            )}
          </div>
        </div>
        {/* Mobile usage chip */}
        <div className="md:hidden max-w-7xl mx-auto px-6 pb-3">
          <UsageChip onOpenUpgrade={() => setShowUpgrade({ open: true, reason: "manual" })} />
        </div>
        {/* Tabs */}
        <nav
          data-testid="main-tabs"
          className="max-w-7xl mx-auto px-6 md:px-10 pb-0 flex gap-1"
        >
          {[
            { id: "detector", label: "Paste URL" },
            { id: "scan", label: "Scan the Article" },
          ].map((t) => {
            const isActive = activeTab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                data-testid={`tab-${t.id}`}
                onClick={() => setActiveTab(t.id)}
                className={`font-mono-alt text-[10.5px] tracking-[0.22em] uppercase px-4 py-3 border-b transition-colors ${
                  isActive
                    ? "text-[color:var(--ts-accent)] border-[color:var(--ts-accent)]"
                    : "text-[color:var(--ts-text-muted)] border-transparent hover:text-[color:var(--ts-text)]"
                }`}
              >
                {t.label}
              </button>
            );
          })}
        </nav>
      </header>

      {activeTab === "scan" ? (
        <ScanArticle onQuotaError={handleQuotaError} applyUsage={applyUsage} />
      ) : (
        <>
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-8 md:pt-12 pb-10">
        <span className="ts-eyebrow">Real · Fake · Uncertain</span>
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight mt-6 max-w-4xl">
          A quiet second opinion for{" "}
          <span className="italic text-[color:var(--ts-accent)]">every</span> headline you read.
        </h1>
      </section>

      {/* Main split */}
      <main className="max-w-7xl mx-auto px-6 md:px-10 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
        {/* Input pane */}
        <div className="lg:col-span-7">
          {/* URL fetcher */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <span className="ts-eyebrow">Fetch from URL</span>
              <span className="font-mono-alt text-[10px] tracking-[0.22em] text-[color:var(--ts-text-muted)] uppercase">
                Recommended
              </span>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Link2
                  size={13}
                  strokeWidth={1.6}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-[color:var(--ts-text-muted)]"
                />
                <input
                  data-testid="url-input"
                  type="url"
                  inputMode="url"
                  autoComplete="off"
                  placeholder="https://www.example.com/some-article"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      fetchFromUrl();
                    }
                  }}
                  className="w-full bg-transparent border border-[color:var(--ts-border)] pl-11 pr-4 py-3.5 text-[14px] font-light text-[color:var(--ts-text)] placeholder:text-[color:#55555a] placeholder:italic focus:outline-none focus:border-[color:var(--ts-accent-dim)] transition-colors"
                />
              </div>
              <button
                data-testid="fetch-url-button"
                type="button"
                onClick={fetchFromUrl}
                disabled={fetching || loading}
                className="ts-btn-ghost inline-flex items-center justify-center gap-2 sm:min-w-[160px]"
              >
                {fetching ? (
                  <Loader2 size={13} strokeWidth={1.8} className="animate-spin text-[color:var(--ts-accent)]" />
                ) : (
                  <Download size={12} strokeWidth={1.6} />
                )}
                {fetching ? "Fetching…" : "Fetch Article"}
              </button>
            </div>
            {source && (
              <div
                data-testid="source-meta"
                className="ts-fade-up mt-4 ts-panel-raised p-4 flex flex-wrap items-center gap-x-5 gap-y-2"
              >
                <span className="font-mono-alt text-[10px] tracking-[0.22em] text-[color:var(--ts-accent)] uppercase">
                  Source
                </span>
                {source.title && (
                  <span className="font-serif-display text-[16px] italic leading-tight">
                    {source.title}
                  </span>
                )}
                <div className="flex items-center gap-3 font-mono-alt text-[10.5px] tracking-[0.14em] uppercase text-[color:var(--ts-text-muted)] ml-auto">
                  {source.site && <span>{source.site}</span>}
                  {source.author && (
                    <>
                      <span className="opacity-40">·</span>
                      <span>{source.author}</span>
                    </>
                  )}
                  {source.date && (
                    <>
                      <span className="opacity-40">·</span>
                      <span>{source.date}</span>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="ts-divider mb-8" />

          <div className="flex items-center justify-between mb-4">
            <span className="ts-eyebrow">Article Text</span>
            <div className="flex items-center gap-4 font-mono-alt text-[10.5px] tracking-[0.18em] text-[color:var(--ts-text-muted)] uppercase">
              <span data-testid="word-count">{words} words</span>
              <span className="opacity-40">·</span>
              <span data-testid="char-count">{chars} chars</span>
            </div>
          </div>

          <textarea
            data-testid="article-textarea"
            className="ts-textarea"
            placeholder="Paste the full body of a news article here — headline, dateline, and body. The more context you provide, the sharper the analysis."
            value={text}
            onChange={(e) => setText(e.target.value)}
            spellCheck={false}
          />

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <button
              data-testid="analyze-button"
              onClick={analyze}
              disabled={!canAnalyze}
              className="ts-btn-primary inline-flex items-center gap-2"
            >
              {loading ? (
                <Loader2 size={13} strokeWidth={1.8} className="animate-spin" />
              ) : (
                <Sparkles size={13} strokeWidth={1.6} />
              )}
              {loading ? "Analyzing…" : "Analyze Article"}
            </button>
            <button
              data-testid="sample-button"
              onClick={loadSample}
              className="ts-btn-ghost inline-flex items-center gap-2"
              type="button"
            >
              Load Sample
            </button>
            <button
              data-testid="clear-button"
              onClick={clearAll}
              className="ts-btn-ghost inline-flex items-center gap-2"
              type="button"
            >
              <RefreshCw size={12} strokeWidth={1.6} />
              Clear
            </button>
            {result && (
              <button
                data-testid="copy-button"
                onClick={copyResult}
                className="ts-btn-ghost inline-flex items-center gap-2 ml-auto"
                type="button"
              >
                <Copy size={12} strokeWidth={1.6} />
                Copy Report
              </button>
            )}
          </div>

          <p className="mt-6 text-xs text-[color:var(--ts-text-muted)] font-light leading-relaxed max-w-xl">
            News Classifier is an analytical aid, not an oracle. Always verify against primary sources
            before drawing conclusions from any single classification.
          </p>

          {/* Annotated article with highlighted cues */}
          {result && result.red_flag_evidence &&
            result.red_flag_evidence.some((slot) => (slot || []).length > 0) && (
              <div className="mt-16" data-testid="annotated-article">
                <div className="ts-divider mb-8" />
                <div className="flex items-center justify-between mb-4">
                  <span className="ts-eyebrow">Highlighted Cues</span>
                  <span className="font-mono-alt text-[10.5px] tracking-[0.18em] uppercase text-[color:var(--ts-accent)]">
                    {result.red_flag_evidence.reduce(
                      (n, s) => n + (s ? s.length : 0),
                      0
                    )}{" "}
                    cues
                  </span>
                </div>
                <div className="ts-panel p-8 md:p-10">
                  <p className="text-xs text-[color:var(--ts-text-muted)] font-light mb-6 leading-relaxed">
                    Passages the model flagged as contributing to its verdict.
                    Underlined text is the exact quote pulled from your article.
                  </p>
                  <div className="ts-article-body whitespace-pre-wrap text-[15px] leading-[1.8] font-light text-[color:var(--ts-text)]">
                    {buildAnnotatedNodes(
                      text,
                      result.red_flag_evidence.flat()
                    )}
                  </div>
                </div>
              </div>
            )}
        </div>

        {/* Analysis pane */}
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-8">
            <div className="flex items-center justify-between mb-4">
              <span className="ts-eyebrow">Analysis</span>
              {result && (
                <span
                  data-testid="verdict-badge"
                  className={`ts-chip ${verdictClass(result.verdict)}`}
                >
                  <VerdictIcon v={result.verdict} className="w-3 h-3" />
                  {result.verdict}
                </span>
              )}
            </div>
            {renderAnalysisPane()}
          </div>
        </div>
      </main>
        </>
      )}

      {/* Footer */}
      <footer className="border-t border-[color:var(--ts-border)] mt-auto">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-8 flex items-center justify-between gap-4 font-mono-alt text-[10.5px] tracking-[0.22em] text-[color:var(--ts-text-muted)] uppercase">
          <div>© News Classifier</div>
        </div>
      </footer>

      {/* Auth + billing overlays */}
      <UpgradeModal
        open={showUpgrade.open}
        reason={showUpgrade.reason}
        onClose={() => setShowUpgrade({ open: false, reason: "manual" })}
      />
      <SignInWall
        open={showSignInWall}
        onClose={() => setShowSignInWall(false)}
        onOpenUpgrade={() => { setShowSignInWall(false); setShowUpgrade({ open: true, reason: "manual" }); }}
      />
    </div>
  );
}

// Router-aware wrapper: mounts AuthProvider and handles the OAuth callback
// (#session_id=…) + payment success/cancel routes.
function AppShellWithHash() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return <AppShell />;
}

export default function App() {
  return (
    <AuthProvider>
      <Routes>
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
        <Route path="*" element={<AppShellWithHash />} />
      </Routes>
    </AuthProvider>
  );
}
